import React, { Fragment, useState, useEffect } from "react";
import { Button, Row, Col, Form, Input, Select, Checkbox, message } from 'antd';
import { useAddRole, useEditRole, useGetRoleById } from "hooks/role/roles";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Image from 'helpers/Image';
import "../style.css";

/**
 * Screens check permissions by `permissionName` (hooks/permissions), so every
 * entry must stay in the list with its API name unchanged. These only change
 * what the table shows: legacy names are relabelled, and pages for the
 * never-deployed Bluetooth side are hidden but still submitted.
 */
const PERMISSION_LABELS = {
    Department: "Contractor",
    Gateway: "RFID Readers",
    Zone: "Site",
    Beacon: "RFID Cards",
    Shift: "Shifts",
};
const HIDDEN_PERMISSIONS = ["Floor", "Alert"];

const FormRoles = () => {


    const editData = useLocation();
    const [serverErrors, setServerErrors] = useState({});
    const create = editData.pathname === "/admin/roles/create";
    const fieldSpan = create ? 24 : 12;
    const [form] = Form.useForm();
    let navigate = useNavigate();

    const [permission, setPermission] = useState({
        permissionAddReqDtoList: [
            {
                permissionName: "Dashboard",
                actionAddReqDto: {
                    isAdd: true,
                    isEdit: true,
                    isDelete: true,
                    isView: false
                }
            },
            {
                permissionName: "Report",
                actionAddReqDto: {
                    isAdd: true,
                    isEdit: true,
                    isDelete: true,
                    isView: false
                }
            },
            {
                permissionName: "Roles",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "User",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Profile",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Employee",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            }, {
                permissionName: "Department",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Floor",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Gateway",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Zone",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            }, {
                permissionName: "Beacon",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Alert",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            },
            {
                permissionName: "Shift",
                actionAddReqDto: {
                    isAdd: false,
                    isEdit: false,
                    isDelete: false,
                    isView: false
                }
            }
        ]
    })

    const { mutate: addRole, isLoading: Loading } = useAddRole();
    const { mutate: editRole, isLoading: editLoading } = useEditRole();
    const { mutate: getRole } = useGetRoleById();

    const status = [
        { 'label': "Active", "value": "active" },
        { 'label': "InActive", "value": "inactive" },
    ]

    const getRoleById = (id) => {
        getRole(id, {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    form.setFieldsValue({
                        "roleName": item?.data?.roleName,
                        "active": item?.data?.active === true ? "active" : "inactive"
                    })
                    // A new object, so React re-renders. Mutating the old one left the defaults on screen.
                    if (item?.data?.permissionAddReqDtoList?.length) {
                        setPermission({ permissionAddReqDtoList: item.data.permissionAddReqDtoList });
                    }
                }
                else {
                    message.error(item?.message);
                }
            },
        });
    }

    const handleOnChange = (e, index, fieldName) => {
        const { checked } = e.target;
        handleCheckboxChange(index, fieldName, checked);
    };

    const handleCheckboxChange = (index, fieldName, checked) => {
        setPermission((prevState) => {
            const updatedList = prevState.permissionAddReqDtoList.map((item, i) => {
                if (index == null || i === index) {
                    return {
                        ...item,
                        actionAddReqDto: {
                            ...item.actionAddReqDto,
                            [fieldName]: ((item.permissionName === "Dashboard" || item.permissionName === "Report") && fieldName !== "isView") ? true : checked
                        }
                    };
                }
                return item;
            });
            return { ...prevState, permissionAddReqDtoList: updatedList };
        });
    };

    const checkAll = (fieldName) => {
        return !permission.permissionAddReqDtoList.some((item) =>
            !HIDDEN_PERMISSIONS.includes(item.permissionName) && item.actionAddReqDto[fieldName] === false);
    }

    const onFinish = (formValues) => {
        var submitData = {
            roleId: create ? 0 : editData.state.roleId,
            roleName: formValues?.roleName.trim(),
            active: create ? 1 : formValues?.active === "active" ? 1 : 0,
            permissionAddReqDtoList: permission.permissionAddReqDtoList
        };
        let formData = JSON.stringify(submitData);
        if (create === true) {
            addRole(formData, {
                onSuccess: (item) => {
                    if (item?.statusCode === 200) {
                        message.success(item?.message)
                        navigate("/admin/roles");
                    }
                    else {
                        message.error(item?.message)
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.message) {
                        const errorMessages = error.response.data.message;
                        setServerErrors(errorMessages);
                    }
                }
            });
        } else {
            editRole(formData, {
                onSuccess: (item) => {
                    if (item?.statusCode === 200) {
                        message.success(item?.data)
                        navigate("/admin/roles");
                    }
                    else {
                        message.error(item?.message || item?.data)
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.message) {
                        const errorMessages = error.response.data.message;
                        setServerErrors(errorMessages);
                    }
                }
            });
        }
    }

    const clearError = (name) => {
        setServerErrors(prev => ({ ...prev, [name]: "" }));
    };


    useEffect(() => {
        if (!create) { getRoleById(editData.state.roleId) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            <Form
                className="form-page"
                form={form}
                onFinish={onFinish}
                colon={false}
                layout="vertical">
                <div className="form-head form-head-sticky">
                    <div className="form-head-left">
                        <Link to="/admin/roles" className="form-back">
                            <Image name="backArrow.svg" />
                        </Link>
                        <div className="form-title">
                            <h1>{create ? "New role" : `${editData.state.roleName}`}</h1>
                            <p>{create ? "Create" : "Edit"} user with roles &amp; responsibilities</p>
                        </div>
                    </div>
                    <div className="form-actions">
                        <Button className="form-cancel" onClick={() => navigate("/admin/roles")}>Cancel</Button>
                        <Button className="form-submit" htmlType="submit" loading={Loading || editLoading}>
                            <Image className="save-img" name="save.svg" />{create ? "Save" : "Update"}
                        </Button>
                    </div>
                </div>

                <div className="form-card">
                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>Role details</h2>
                            <p>The name this role is listed under when assigning users.</p>
                        </div>
                        <Row gutter={[24, 0]}>
                            <Col xs={24} sm={fieldSpan}>
                                <Form.Item label="Role Name" name="roleName" rules={[{ required: true, message: "Enter Role Name" }]}
                                    validateStatus={serverErrors?.roleName ? "error" : ""}
                                    help={serverErrors?.roleName?.[0] || ""}
                                >
                                    <Input placeholder="Enter the role name" maxLength={40} onChange={() => clearError("roleName")}></Input>
                                </Form.Item>
                            </Col>
                            {create ? null : (
                                <Col xs={24} sm={fieldSpan}>
                                    <Form.Item label="Status" name="active" rules={[{ required: true }]}>
                                        <Select placeholder={"Select status"} options={status} />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </section>

                    <section className="form-section">
                        <div className="form-section-head">
                            <h2>Permissions</h2>
                            <p>Choose what this role can do on each page.</p>
                        </div>
                        <div className="common-table check-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Page name</th>
                                        <th>Add <Checkbox checked={checkAll("isAdd")} onChange={(e) => handleOnChange(e, null, "isAdd")} /></th>
                                        <th>Edit <Checkbox checked={checkAll("isEdit")} onChange={(e) => handleOnChange(e, null, "isEdit")} /></th>
                                        <th>Delete <Checkbox checked={checkAll("isDelete")} onChange={(e) => handleOnChange(e, null, "isDelete")} /></th>
                                        <th>View <Checkbox checked={checkAll("isView")} onChange={(e) => handleOnChange(e, null, "isView")} /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {permission.permissionAddReqDtoList === null || permission.permissionAddReqDtoList === undefined || permission.permissionAddReqDtoList.length === 0 ? null : permission.permissionAddReqDtoList.map((item, a) => HIDDEN_PERMISSIONS.includes(item.permissionName) ? null : (
                                        <tr key={a}>
                                            <td>{PERMISSION_LABELS[item.permissionName] || item.permissionName}</td>
                                            <td>
                                                <Checkbox style={{ paddingLeft: 25 }} disabled={item.permissionName === "Dashboard" || item.permissionName === "Report"} checked={(item.permissionName === "Dashboard" || item.permissionName === "Report") ? false : item.actionAddReqDto.isAdd} onChange={(e) => handleOnChange(e, a, "isAdd")} />
                                            </td>
                                            <td>
                                                <Checkbox style={{ paddingLeft: 25 }} disabled={item.permissionName === "Dashboard" || item.permissionName === "Report"} checked={(item.permissionName === "Dashboard" || item.permissionName === "Report") ? false : item.actionAddReqDto.isEdit} onChange={(e) => handleOnChange(e, a, "isEdit")} />
                                            </td>
                                            <td>
                                                <Checkbox style={{ paddingLeft: 40 }} disabled={item.permissionName === "Dashboard" || item.permissionName === "Report"} checked={(item.permissionName === "Dashboard" || item.permissionName === "Report") ? false : item.actionAddReqDto.isDelete} onChange={(e) => handleOnChange(e, a, "isDelete")} />
                                            </td>
                                            <td>
                                                <Checkbox style={{ paddingLeft: 30 }} checked={item.actionAddReqDto.isView} onChange={(e) => handleOnChange(e, a, "isView")} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>

            </Form>
        </Fragment>
    )
}
export default FormRoles;
